import React, { useState, useEffect } from 'react';
import { hasPermission } from '../utils/permissions';
import { Switch, FormControlLabel } from '@mui/material';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
// @ts-ignore
import { StandardPageLayout, Button as CommonButton, LoadingSpinner, Modal, ConfirmModal, FormInput, FormTextarea, FormSelect } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { useNotification } from '../context/NotificationContext.jsx';
import { formatToLocal24h, getActiveTimezone } from '../utils/dateTime.js';
import {
    PlusCircle, Search, LayoutGrid, List, MoreHorizontal,
    Play, Pause, Trash2, Edit, Loader2, Calendar, Clock,
    Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import EventListenersTab from './EventListenersTab';

const FREQUENCY_OPTIONS = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
];

// Unified content types for the scheduler picker
const CONTENT_TYPES = [
    { value: 'datamodel', label: 'Data Model/Set', emoji: '🗄️', jobType: 'datamodel_refresh' },
    { value: 'flow', label: 'Flow / App', emoji: '⚡', jobType: 'flow_execution' },
    { value: 'ml_model', label: 'ML Model', emoji: '🧠', jobType: 'flow_execution' },
    { value: 'dashboard', label: 'Dashboard', emoji: '📊', jobType: 'dashboard_refresh' },
];

// Map job_type string → display label (includes legacy types for backward compat)
const JOB_TYPE_LABELS = {
    'datamodel_refresh': '🔄 Data Model',
    'flow_execution': '⚡ Flow / App',
    'dashboard_refresh': '📊 Dashboard',
    'ML Model': '🧠 ML Model (legacy)',
    'GenAI Usecase': '🤖 GenAI (legacy)',
    'Dataset Refresh': '🗄️ Dataset (legacy)',
};

// Shared style tokens for uniform form-field appearance across the dialog
const FIELD_CLS = 'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:text-white';
const FIELD_READONLY_CLS = 'w-full rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-300';

const AutomationsPage = ({ token, currentUser }) => {
    // Admin can see all tenants' schedules — any user with cross-tenant view permission
    const isAdmin = hasPermission(currentUser, 'tenants:view_all');

    // Automation Tabs State
    const [activeTab, setActiveTab] = useState('time-based');

    // Admin State
    const [showAllTenants, setShowAllTenants] = useState(false);
    const [filterTenantId, setFilterTenantId] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const { addNotification } = useNotification();
    // API returns { schedules: [...], total: N }
    const { data: schedulesData, isLoading: loadingSchedules, refresh, setData: setSchedulesData } = useData(
        isAdmin && showAllTenants ? '/scheduler?all_tenants=true' : '/scheduler',
        token
    );

    // Fetch Tenants for Admin Filter
    const { data: tenantsList } = useData(isAdmin ? '/tenants' : null, token);
    const tenantsMap = (tenantsList || []).reduce((acc, t) => ({ ...acc, [t.id]: t.name }), {});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    // Admin Context: Target Tenant
    const [targetTenantId, setTargetTenantId] = useState(isAdmin ? null : currentUser?.tenantId);

    // Initial State for Form
    const initialFormState = {
        name: '',
        description: '',
        job_type: 'datamodel_refresh',
        job_config: {},
        // Scheduling Logic
        scheduleMode: 'recurring',
        frequency: 'daily',
        time: '09:00',
        weekDays: [],
        monthDay: 1,
        intervalValue: 1,
        intervalUnit: 'hours',
        cronExpression: '0 12 * * *'
    };

    const [formData, setFormData] = useState(initialFormState);
    const [runningMap, setRunningMap] = useState({});

    // Content picker state
    const [selectedContentType, setSelectedContentType] = useState('datamodel'); // datamodel|flow|ml_model|dashboard
    const [selectedItemId, setSelectedItemId] = useState('');  // the chosen content id (flow_id or content_id)
    const [schedulableItems, setSchedulableItems] = useState([]); // list loaded from /scheduler/schedulable-content
    const [loadingItems, setLoadingItems] = useState(false);
    const [viewLayout, setViewLayout] = useState('list'); // 'grid' | 'list'

    const schedules = schedulesData?.schedules || [];

    // Filter Logic
    const filteredSchedules = schedules.filter(schedule => {
        const matchesSearch = schedule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            schedule.job_type.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesTenant = filterTenantId === 'all' || schedule.tenant_id === filterTenantId;

        return matchesSearch && matchesTenant;
    });


    // ── Content picker: fetch items when tenant or content type changes ─────────
    const fetchSchedulableItems = React.useCallback(async (contentType, tenantId) => {
        if (!contentType) return;
        setLoadingItems(true);
        try {
            const params = new URLSearchParams({ content_type: contentType });
            // 'system' = Platform scope (no tenant), any other value = tenant id
            if (tenantId && tenantId !== 'system') params.append('tenant_id', tenantId);
            const data = await apiFetch(`/scheduler/schedulable-content?${params}`, token);
            setSchedulableItems(data?.items || []);
        } catch (e) {
            setSchedulableItems([]);
        } finally {
            setLoadingItems(false);
        }
    }, [token]);

    // Refetch when content type or tenant changes (modal open)
    useEffect(() => {
        if (!isModalOpen) return;
        // Admin must explicitly choose a workspace first
        // Skip the guard when editing — workspace is already set from the schedule
        if (isAdmin && targetTenantId === null && !editingSchedule) {
            setSchedulableItems([]);
            return;
        }
        if (targetTenantId === null) return; // still hasn't resolved
        fetchSchedulableItems(selectedContentType, targetTenantId);
    }, [selectedContentType, targetTenantId, isModalOpen, fetchSchedulableItems, isAdmin, editingSchedule]);

    // --- Form init / edit mode ---
    useEffect(() => {
        if (editingSchedule) {
            // Set Target Tenant (Admin Mode)
            if (isAdmin) {
                // null tenant_id = Platform (System) scope → use sentinel 'system'
                setTargetTenantId(editingSchedule.tenant_id || 'system');
            }

            // Determine UI mode based on existing schedule
            let mode = 'custom';
            let freq = 'daily';
            let time = '09:00';
            let intervalVal = 1;
            let intervalUnit = 'hours';
            let weekDays = [];
            let monthDay = 1;
            let cronExpr = editingSchedule.cron_expression || '';

            if (editingSchedule.interval_seconds) {
                mode = 'interval';
                if (editingSchedule.interval_seconds % 86400 === 0) {
                    intervalVal = editingSchedule.interval_seconds / 86400;
                    intervalUnit = 'days';
                } else if (editingSchedule.interval_seconds % 3600 === 0) {
                    intervalVal = editingSchedule.interval_seconds / 3600;
                    intervalUnit = 'hours';
                } else {
                    intervalVal = Math.floor(editingSchedule.interval_seconds / 60);
                    intervalUnit = 'minutes';
                }
            } else if (editingSchedule.cron_expression) {
                const parts = editingSchedule.cron_expression.split(' ');
                if (parts.length === 5) {
                    const [min, hour, dom, mon, dow] = parts;
                    const formatTime = (h, m) => `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                    if (dom === '*' && mon === '*' && dow === '*') {
                        if (hour === '*') { mode = 'recurring'; freq = 'hourly'; }
                        else { mode = 'recurring'; freq = 'daily'; time = formatTime(hour, min); }
                    } else if (min !== '*' && hour !== '*' && dom === '*' && mon === '*' && dow !== '*') {
                        mode = 'recurring'; freq = 'weekly'; time = formatTime(hour, min); weekDays = dow.split(',');
                    } else if (min !== '*' && hour !== '*' && dom !== '*' && mon === '*' && dow === '*') {
                        mode = 'recurring'; freq = 'monthly'; time = formatTime(hour, min); monthDay = parseInt(dom);
                    }
                }
            }

            setFormData({
                name: editingSchedule.name || '',
                job_type: editingSchedule.job_type || 'datamodel_refresh',
                job_config: editingSchedule.job_config || {},
                scheduleMode: mode, frequency: freq, time, weekDays, monthDay,
                intervalValue: intervalVal, intervalUnit, cronExpression: cronExpr
            });

            // Pre-fill new content-type picker from job_type
            const JOB_TYPE_TO_CONTENT = {
                'datamodel_refresh': 'datamodel',
                'flow_execution': 'flow',
                'dashboard_refresh': 'dashboard',
                'ML Model': 'ml_model',
                'GenAI Usecase': 'flow',
                'Dataset Refresh': 'datamodel',
            };
            const detectedType = JOB_TYPE_TO_CONTENT[editingSchedule.job_type] || 'datamodel';
            setSelectedContentType(detectedType);
            // Pre-fill selected item id
            const cfg = editingSchedule.job_config || {};
            setSelectedItemId(cfg.flow_id || cfg.content_id || cfg.database_id || cfg.dataset_id || '');

        } else {
            // Creating New
            setFormData(initialFormState);
            setSelectedContentType('datamodel');
            setSelectedItemId('');
            setSchedulableItems([]);
            if (isAdmin && filterTenantId !== 'all') {
                setTargetTenantId(filterTenantId);
            } else if (isAdmin) {
                setTargetTenantId(null);
            }
        }
    }, [editingSchedule, filterTenantId]);


    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isAdmin && !targetTenantId && !editingSchedule) {
            // Safety check: Admin creating new schedule must generally select tenant context first, or we default to global?
            // Assuming payload doesn't need tenant_id because backend reads it from token OR endpoint.
            // Wait: POST /scheduler infers tenant from token. Admin can't create for others yet easily via this UI.
            // BUT, user asked about EDITING. I will focus on editing safety.
            // If editing, use existing logic.
        }

        // Construct Payload (Shared logic)
        let cron_expression = null;
        let interval_seconds = null;

        if (formData.scheduleMode === 'custom') {
            cron_expression = formData.cronExpression;
            if (!cron_expression) {
                addNotification("Cron expression is required", "error");
                return;
            }
        } else if (formData.scheduleMode === 'interval') {
            interval_seconds = formData.intervalUnit === 'hours'
                ? formData.intervalValue * 3600
                : formData.intervalUnit === 'days'
                    ? formData.intervalValue * 86400
                    : formData.intervalValue * 60;
        } else {
            // Recurring Builder
            const [hour, minute] = formData.time.split(':');

            if (formData.frequency === 'hourly') {
                cron_expression = `0 * * * *`;
            } else if (formData.frequency === 'daily') {
                cron_expression = `${Number(minute)} ${Number(hour)} * * *`;
            } else if (formData.frequency === 'weekly') {
                const dow = formData.weekDays.length > 0 ? formData.weekDays.join(',') : '*';
                cron_expression = `${Number(minute)} ${Number(hour)} * * ${dow}`;
            } else if (formData.frequency === 'monthly') {
                cron_expression = `${Number(minute)} ${Number(hour)} ${formData.monthDay} * *`;
            }
        }

        const payload = {
            name: formData.name,
            job_type: formData.job_type,
            schedule_type: interval_seconds ? 'interval' : 'cron',
            cron_expression: cron_expression,
            interval_seconds: interval_seconds,
            job_config: {
                // Map the selected item to the correct job_config field per content type
                ...(selectedContentType === 'datamodel'
                    ? { database_id: selectedItemId }
                    : selectedContentType === 'dashboard'
                        ? { content_id: selectedItemId }
                        : { flow_id: selectedItemId }  // flow, ml_model
                ),
                // Preserve any existing config fields not overwritten above
                ...Object.fromEntries(
                    Object.entries(formData.job_config || {}).filter(
                        ([k]) => !['flow_id', 'content_id', 'instance_id', 'dataset_id'].includes(k)
                    )
                ),
            },
            is_active: true,
            timezone: getActiveTimezone()
        };

        if (isAdmin) {
            // Handle System selection (targetTenantId === 'system' -> null)
            // Handle Tenant selection (targetTenantId === 'uuid' -> 'uuid')
            // Handle Default/Not selected? We initialized targetTenantId.
            // If creating new: use selection.
            // If editing: we are sending PUT, tenant_id update might not be allowed/supported by backend update_schedule yet?
            // Backend update_schedule uses payload.dict(exclude_unset). Schema has tenant_id? No, ScheduleUpdate doesn't have tenant_id.
            // So we only need to add this for CREATE (POST).
            if (!editingSchedule && targetTenantId) {
                payload.tenant_id = targetTenantId === 'system' ? null : targetTenantId;
            }
        }


        const action = editingSchedule ? 'update' : 'create';
        try {
            let result;
            if (editingSchedule) {
                result = await apiFetch(`/scheduler/${editingSchedule.id}`, token, {
                    method: 'PUT',
                    body: JSON.stringify(payload)
                });
                // Optimistic update: replace the edited schedule in local state immediately
                setSchedulesData(prev => ({
                    ...prev,
                    schedules: (prev?.schedules || []).map(s =>
                        s.id === editingSchedule.id ? { ...s, ...result } : s
                    ),
                }));
            } else {
                result = await apiFetch('/scheduler', token, {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
                // Optimistic update: prepend the new schedule to local state immediately
                // NOTE: Do NOT call refresh() here — the POST response IS the server state.
                // A background GET re-fetch would overwrite this with filtered/empty data
                // since admin GET /scheduler only returns platform-level schedules by default.
                setSchedulesData(prev => ({
                    schedules: [result, ...(prev?.schedules || [])],
                    total: (prev?.total || 0) + 1,
                }));

                // For admin: if a tenant-scoped schedule was just created,
                // switch to "All Tenants" view so future refreshes show it
                if (isAdmin && result.tenant_id) {
                    setShowAllTenants(true);
                    if (result.tenant_id !== filterTenantId) {
                        setFilterTenantId('all');
                    }
                }
            }
            setIsModalOpen(false);
            addNotification(`Schedule ${action}d successfully.`);
        } catch (err) {
            addNotification(`Failed to ${action} schedule: ${err.message}`, 'error');
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await apiFetch(`/scheduler/${confirmDelete.id}`, token, { method: 'DELETE' });
            // Optimistic update: remove from local state immediately
            setSchedulesData(prev => ({
                schedules: (prev?.schedules || []).filter(s => s.id !== confirmDelete.id),
                total: Math.max((prev?.total || 1) - 1, 0),
            }));
            addNotification('Schedule deleted successfully.');
            setConfirmDelete(null);
        } catch (err) {
            addNotification(err.message, 'error');
            refresh(); // Re-fetch on error to sync state
        }
    };

    const handleToggleStatus = async (schedule) => {
        const action = schedule.is_active ? 'pause' : 'resume';
        try {
            await apiFetch(`/scheduler/${schedule.id}/${action}`, token, { method: 'POST' });
            addNotification(`Schedule ${action}d successfully.`);
            refresh();
        } catch (err) {
            addNotification(`Failed to ${action} schedule: ${err.message}`, 'error');
        }
    };

    const openCreateModal = () => {
        setEditingSchedule(null);
        setIsModalOpen(true);
    };


    // Helper: Display content name on cards/rows
    // Backend resolves content_name from DataModel/Flow tables.
    const getContentName = (schedule) => {
        if (schedule.content_name) return schedule.content_name;
        // Fallback: truncated ID from job_config (legacy, should not normally reach here)
        const cfg = schedule.job_config || {};
        const targetId = cfg.flow_id || cfg.content_id || cfg.database_id || cfg.dataset_id;
        if (!targetId) return '-';
        return targetId.length > 20 ? `${targetId.slice(0, 16)}…` : targetId;
    };


    // Helper: Convert cron/interval to human-readable schedule description
    const getDisplaySchedule = (schedule) => {
        // Interval-based schedules
        if (schedule.interval_seconds) {
            const s = schedule.interval_seconds;
            if (s % 86400 === 0) return `Every ${s / 86400} Day${s / 86400 > 1 ? 's' : ''}`;
            if (s % 3600 === 0) return `Every ${s / 3600} Hr${s / 3600 > 1 ? 's' : ''}`;
            if (s % 60 === 0) return `Every ${s / 60} Min${s / 60 > 1 ? 's' : ''}`;
            return `Every ${s}s`;
        }

        const cron = schedule.cron_expression;
        if (!cron) return '—';

        const parts = cron.trim().split(/\s+/);
        if (parts.length !== 5) return cron; // non-standard, show raw

        const [min, hour, dom, mon, dow] = parts;
        const pad = (n) => String(n).padStart(2, '0');
        const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        // Every minute: * * * * *
        if (min === '*' && hour === '*' && dom === '*' && mon === '*' && dow === '*')
            return 'Every Minute';

        // Every N minutes: */N * * * *
        const minStep = min.match(/^\*\/(\d+)$/);
        if (minStep && hour === '*' && dom === '*' && mon === '*' && dow === '*')
            return `Every ${minStep[1]} Min${minStep[1] > 1 ? 's' : ''}`;

        // Every N hours: 0 */N * * *  or  M */N * * *
        const hourStep = hour.match(/^\*\/(\d+)$/);
        if (hourStep && dom === '*' && mon === '*' && dow === '*')
            return `Every ${hourStep[1]} Hr${hourStep[1] > 1 ? 's' : ''}`;

        // Hourly at minute M: M * * * *
        if (hour === '*' && dom === '*' && mon === '*' && dow === '*')
            return `Hourly at :${pad(min)}`;

        // Daily: M H * * *
        if (dom === '*' && mon === '*' && dow === '*' && !hour.includes('*') && !min.includes('*'))
            return `Daily at ${pad(hour)}:${pad(min)}`;

        // Weekly: M H * * DOW
        if (dom === '*' && mon === '*' && dow !== '*' && !hour.includes('*') && !min.includes('*')) {
            const dayLabels = dow.split(',')
                .map(d => DAY_NAMES[parseInt(d)] || d)
                .join(', ');
            return `Weekly ${dayLabels} at ${pad(hour)}:${pad(min)}`;
        }

        // Monthly: M H D * *
        if (dow === '*' && mon === '*' && dom !== '*' && !hour.includes('*') && !min.includes('*'))
            return `Monthly on ${dom} at ${pad(hour)}:${pad(min)}`;

        // Fallback: show raw but nicely trimmed
        return cron;
    };

    // Header Components
    const headerActions = (
        <CommonButton onClick={openCreateModal} sx={{ height: 30 }}>
            <PlusCircle className="mr-2 h-4 w-4" /> NEW SCHEDULE
        </CommonButton>
    );

    const filterControls = (
        <div className="flex items-center gap-4">
            <div className="relative max-w-md group w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-[#002F5D] dark:text-slate-500 transition-colors" />
                <Input
                    type="text"
                    placeholder="Search schedules..."
                    className="pl-10 bg-white dark:bg-slate-900"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {isAdmin && (
                <div className="flex items-center gap-4">
                    <Select
                        value={filterTenantId}
                        onValueChange={setFilterTenantId}
                        disabled={!showAllTenants}
                    >
                        <SelectTrigger className="w-[180px] h-9 text-sm bg-white dark:bg-slate-900">
                            <SelectValue placeholder="All Tenants" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Tenants</SelectItem>
                            {tenantsList?.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showAllTenants}
                                onChange={(e) => setShowAllTenants(e.target.checked)}
                                size="small"
                            />
                        }
                        label={<span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">All Tenants</span>}
                        className="mr-0"
                    />
                </div>
            )}

            <div className="flex items-center bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800 p-1 ml-auto">
                <Button
                    variant={viewLayout === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={`px-2 h-8 ${viewLayout === 'grid' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setViewLayout('grid')}
                >
                    <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                    variant={viewLayout === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={`px-2 h-8 ${viewLayout === 'list' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setViewLayout('list')}
                >
                    <List className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );

    const renderCard = (schedule) => (
        <div key={schedule.id} className="group bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer relative overflow-hidden flex flex-col h-full">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />

            <div className="flex justify-between items-start mb-4">
                <div className={`flex items-center justify-center p-2.5 rounded-xl group-hover:text-white transition-colors duration-300 ${schedule.is_active ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 group-hover:bg-green-600' : 'bg-gray-50 dark:bg-gray-800 text-gray-400 group-hover:bg-gray-500'}`}>
                    <Calendar className="h-6 w-6" />
                </div>
                <div className="flex gap-1">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                <MoreHorizontal className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40 bg-white dark:bg-slate-900 shadow-xl border-gray-100 dark:border-slate-800">
                            {hasPermission(currentUser, 'content:manage') && (
                                <>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingSchedule(schedule); setIsModalOpen(true); }} disabled={schedule.tenant_id !== currentUser?.tenantId && !isAdmin}>
                                        <Edit className="w-4 h-4 mr-2" /> Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleToggleStatus(schedule); }}>
                                        {schedule.is_active ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                                        {schedule.is_active ? 'Pause' : 'Resume'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setConfirmDelete(schedule); }} disabled={schedule.tenant_id !== currentUser?.tenantId && !isAdmin} className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20">
                                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                                    </DropdownMenuItem>
                                </>
                            )}
                            <DropdownMenuItem onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                    setRunningMap(prev => ({ ...prev, [schedule.id]: true }));
                                    await apiFetch(`/scheduler/${schedule.id}/run-now`, token, { method: 'POST' });
                                    addNotification('Schedule triggered successfully.');
                                    setTimeout(refresh, 500);
                                } catch (err) {
                                    addNotification(err.message, 'error');
                                } finally {
                                    setRunningMap(prev => ({ ...prev, [schedule.id]: false }));
                                }
                            }} disabled={(schedule.tenant_id !== currentUser?.tenantId && !isAdmin) || runningMap[schedule.id]}>
                                <Play className="w-4 h-4 mr-2" /> Run Now
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <div className="mb-4 flex-grow">
                <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate" title={schedule.name}>{schedule.name}</h3>
                    {isAdmin && showAllTenants && schedule.tenant_id && (
                        <span className="text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded">
                            {tenantsMap[schedule.tenant_id] || 'Tenant'}
                        </span>
                    )}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">{schedule.job_type}</p>

                <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
                    <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        <span>{getDisplaySchedule(schedule)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-medium">Content:</span> {getContentName(schedule)}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-medium">Next Run:</span>
                        {schedule.next_run
                            ? <span className="text-blue-600 dark:text-blue-400">{formatToLocal24h(schedule.next_run, schedule.timezone)}</span>
                            : <span className="text-slate-400">Not Scheduled</span>}
                    </div>
                </div>
            </div>

            <div className="pt-4 border-t border-gray-50 dark:border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${schedule.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 capitalize">{schedule.is_active ? 'Active' : 'Paused'}</span>
                </div>
                {runningMap[schedule.id] && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
            </div>
        </div>
    );

    const renderListView = (items) => (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <Table>
                <TableHeader className="bg-gray-50/50 dark:bg-slate-900/50">
                    <TableRow className="border-b border-gray-200 dark:border-slate-800">
                        <TableHead className="text-gray-500 dark:text-slate-400">Name</TableHead>
                        {isAdmin && showAllTenants && <TableHead className="text-gray-500 dark:text-slate-400">Tenant</TableHead>}
                        <TableHead className="text-gray-500 dark:text-slate-400">Content</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Type</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Schedule</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Next Run</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Status</TableHead>
                        <TableHead className="text-right text-gray-500 dark:text-slate-400">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map((schedule) => (
                        <TableRow key={schedule.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-gray-100 dark:border-slate-800">
                            <TableCell className="font-medium text-gray-900 dark:text-slate-100">
                                {schedule.name}
                            </TableCell>
                            {isAdmin && showAllTenants && (
                                <TableCell className="text-gray-500 dark:text-slate-400">
                                    {schedule.tenant_id ? (tenantsMap[schedule.tenant_id] || schedule.tenant_id) : <span className="text-blue-500">System</span>}
                                </TableCell>
                            )}
                            <TableCell className="text-gray-500 dark:text-slate-400">{getContentName(schedule)}</TableCell>
                            <TableCell className="text-gray-500 dark:text-slate-400">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                                    {schedule.job_type}
                                </span>
                            </TableCell>
                            <TableCell className="font-mono text-xs text-gray-500 dark:text-slate-400">{getDisplaySchedule(schedule)}</TableCell>
                            <TableCell className="text-gray-500 dark:text-slate-400 text-xs whitespace-nowrap">
                                {schedule.next_run
                                    ? <span className="font-medium text-slate-700 dark:text-slate-300">{formatToLocal24h(schedule.next_run, schedule.timezone)}</span>
                                    : <span className="text-slate-400 text-xs italic">Not Scheduled</span>}
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={!!schedule.is_active}
                                        onChange={() => handleToggleStatus(schedule)}
                                        size="small"
                                    />
                                </div>
                            </TableCell>
                            <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-green-600 dark:hover:text-green-400"
                                        onClick={async () => {
                                            try {
                                                setRunningMap(prev => ({ ...prev, [schedule.id]: true }));
                                                await apiFetch(`/scheduler/${schedule.id}/run-now`, token, { method: 'POST' });
                                                addNotification('Schedule triggered successfully.');
                                                setTimeout(refresh, 500);
                                            } catch (e) {
                                                addNotification(e.message, 'error');
                                            } finally {
                                                setRunningMap(prev => ({ ...prev, [schedule.id]: false }));
                                            }
                                        }}
                                        disabled={(schedule.tenant_id !== currentUser?.tenantId && !isAdmin) || runningMap[schedule.id]}
                                    >
                                        <DynamicIcon
                                            name={runningMap[schedule.id] ? "Loader2" : "Play"}
                                            className={`w-4 h-4 ${runningMap[schedule.id] ? "animate-spin" : ""}`}
                                        />
                                    </Button>
                                    {hasPermission(currentUser, 'content:manage') && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                                    <MoreHorizontal className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => { setEditingSchedule(schedule); setIsModalOpen(true); }} disabled={schedule.tenant_id !== currentUser?.tenantId && !isAdmin}>
                                                    <Edit className="w-4 h-4 mr-2" /> Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => setConfirmDelete(schedule)} disabled={schedule.tenant_id !== currentUser?.tenantId && !isAdmin} className="text-red-600">
                                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );

    return (
        <StandardPageLayout
            title="Scheduler"
            description="Manage time-based schedules and event-based listeners."
        >
            <div className="mb-6 border-b border-gray-200 dark:border-slate-800 pb-2 flex justify-between items-center">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-[400px]">
                    <TabsList className="grid w-full grid-cols-2 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                        <TabsTrigger value="time-based" className={`data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 font-medium`}>
                            <Clock className="w-4 h-4 mr-2" />
                            Time-Based
                        </TabsTrigger>
                        <TabsTrigger value="event-based" className={`data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-purple-600 dark:data-[state=active]:text-purple-400 font-medium`}>
                            <Zap className="w-4 h-4 mr-2" />
                            Event-Based
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {activeTab === 'time-based' && (
                    <div className="flex gap-4">
                        {filterControls}
                        {headerActions}
                    </div>
                )}
            </div>

            <div className="pb-10 min-h-[50vh]">
                {activeTab === 'time-based' ? (
                    loadingSchedules && !schedulesData ? <LoadingSpinner /> : (
                        <>
                            {filteredSchedules.length === 0 ? (
                                <div className="text-center py-12">
                                    <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                                        <Calendar className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                        No schedules found
                                    </h3>
                                    <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
                                        {searchQuery
                                            ? 'Try adjusting your search query'
                                            : 'Create a new schedule to automate your workflows.'}
                                    </p>
                                    <Button onClick={openCreateModal}>
                                        <PlusCircle className="mr-2 h-4 w-4" /> Create Schedule
                                    </Button>
                                </div>
                            ) : (
                                viewLayout === 'list' ? (
                                    renderListView(filteredSchedules)
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {filteredSchedules.map(schedule => renderCard(schedule))}
                                    </div>
                                )
                            )}
                        </>
                    )
                ) : (
                    <EventListenersTab token={token} currentUser={currentUser} isAdmin={isAdmin} />
                )}
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Target Workspace: Platform Admins only — hidden for Tenant Portal users */}
                    {isAdmin && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Workspace</label>
                            {editingSchedule ? (
                                <div className={FIELD_READONLY_CLS}>
                                    {tenantsMap[editingSchedule.tenant_id]
                                        ? `${tenantsMap[editingSchedule.tenant_id]}`
                                        : editingSchedule.tenant_id === null || editingSchedule.tenant_id === undefined
                                            ? 'Platform (System)'
                                            : editingSchedule.tenant_id}
                                </div>
                            ) : (
                                <select
                                    className={FIELD_CLS}
                                    value={targetTenantId || ''}
                                    onChange={(e) => {
                                        const val = e.target.value || null;
                                        setTargetTenantId(val);
                                        setSelectedItemId('');
                                    }}
                                >
                                    <option value="">-- Select Workspace --</option>
                                    <option value="system">🏢 Platform (System)</option>
                                    {tenantsList?.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    <FormInput
                        id="name"
                        name="name"
                        label="Schedule Name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />


                    {/* ── Content Type Picker ──────────────────────────────── */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Content Type
                        </label>
                        <div className="grid grid-cols-4 gap-2 mb-3">
                            {CONTENT_TYPES.map(ct => (
                                <button
                                    key={ct.value}
                                    type="button"
                                    onClick={() => {
                                        setSelectedContentType(ct.value);
                                        setSelectedItemId('');
                                        setFormData(prev => ({ ...prev, job_type: ct.jobType }));
                                    }}
                                    className={`flex flex-col items-center gap-1 px-2 py-3 rounded-xl border-2 text-xs font-semibold transition-all
                                        ${selectedContentType === ct.value
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm'
                                            : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:border-blue-300 hover:bg-gray-50 dark:hover:bg-slate-800'
                                        }`}
                                >
                                    <span className="text-lg">{ct.emoji}</span>
                                    <span>{ct.label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Single content dropdown, loaded from /scheduler/schedulable-content */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                                Select {CONTENT_TYPES.find(c => c.value === selectedContentType)?.label}
                            </label>
                            <select
                                value={selectedItemId}
                                onChange={(e) => {
                                    const id = e.target.value;
                                    setSelectedItemId(id);
                                    // Auto-fill schedule name
                                    const item = schedulableItems.find(i => i.id === id);
                                    if (item && !formData.name) {
                                        const label = CONTENT_TYPES.find(c => c.value === selectedContentType)?.label || '';
                                        setFormData(prev => ({ ...prev, name: `${label}: ${item.name}` }));
                                    }
                                }}
                                className={FIELD_CLS}
                                required
                                disabled={loadingItems}
                            >
                                <option value="">
                                    {loadingItems ? 'Loading...' : `-- Select ${CONTENT_TYPES.find(c => c.value === selectedContentType)?.label} --`}
                                </option>
                                {schedulableItems.map(item => (
                                    <option key={item.id} value={item.id}>{item.name}</option>
                                ))}
                            </select>
                            {schedulableItems.length === 0 && !loadingItems && selectedItemId === '' && (
                                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                    {isAdmin && !targetTenantId
                                        ? 'Select a workspace above to load content.'
                                        : `No ${CONTENT_TYPES.find(c => c.value === selectedContentType)?.label} items found.`}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* --- Trigger Scheduling Section (Tabbed) --- */}
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md border border-gray-200 dark:border-gray-700 space-y-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Schedule Trigger</label>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
                            {['recurring', 'interval', 'custom'].map(mode => (
                                <button
                                    key={mode}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, scheduleMode: mode })}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${formData.scheduleMode === mode
                                        ? 'border-primary text-primary'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                                        }`}
                                >
                                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                                </button>
                            ))}
                        </div>

                        {/* Content */}
                        {formData.scheduleMode === 'recurring' && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Frequency</label>
                                        <select
                                            value={formData.frequency}
                                            onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                                            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                                        >
                                            {FREQUENCY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </div>
                                    {formData.frequency !== 'hourly' && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Time (UTC)</label>
                                            <div className="flex gap-1 items-center">
                                                {/* Hour select: 00–23 */}
                                                <select
                                                    value={formData.time ? formData.time.split(':')[0] : '09'}
                                                    onChange={(e) => {
                                                        const mm = formData.time ? formData.time.split(':')[1] : '00';
                                                        setFormData({ ...formData, time: `${e.target.value}:${mm}` });
                                                    }}
                                                    className={FIELD_CLS}
                                                >
                                                    {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (
                                                        <option key={h} value={h}>{h}</option>
                                                    ))}
                                                </select>
                                                <span className="text-gray-500 font-bold px-0.5">:</span>
                                                {/* Minute select: every 5 min */}
                                                <select
                                                    value={formData.time ? String(Math.round(parseInt(formData.time.split(':')[1] || '0') / 5) * 5 % 60).padStart(2, '0') : '00'}
                                                    onChange={(e) => {
                                                        const hh = formData.time ? formData.time.split(':')[0] : '09';
                                                        setFormData({ ...formData, time: `${hh}:${e.target.value}` });
                                                    }}
                                                    className={FIELD_CLS}
                                                >
                                                    {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0')).map(m => (
                                                        <option key={m} value={m}>{m}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {formData.frequency === 'weekly' && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-2">Days of Week</label>
                                        <div className="flex gap-2">
                                            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, idx) => {
                                                const dayStr = idx.toString();
                                                const isSelected = formData.weekDays.includes(dayStr);
                                                return (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        onClick={() => {
                                                            const newDays = isSelected
                                                                ? formData.weekDays.filter(x => x !== dayStr)
                                                                : [...formData.weekDays, dayStr];
                                                            setFormData({ ...formData, weekDays: newDays });
                                                        }}
                                                        className={`w-8 h-8 rounded-full text-xs font-bold transition-all ${isSelected
                                                            ? 'bg-primary text-white shadow-md transform scale-105'
                                                            : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                                            }`}
                                                    >
                                                        {d}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}

                                {formData.frequency === 'monthly' && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Day of Month</label>
                                        <input
                                            type="number"
                                            min="1" max="31"
                                            value={formData.monthDay}
                                            onChange={(e) => setFormData({ ...formData, monthDay: parseInt(e.target.value) })}
                                            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {formData.scheduleMode === 'interval' && (
                            <div className="flex space-x-2">
                                <div className="flex-1">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Every</label>
                                    <input
                                        type="number"
                                        min="1"
                                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                                        value={formData.intervalValue}
                                        onChange={(e) => setFormData({ ...formData, intervalValue: Number(e.target.value) })}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Unit</label>
                                    <select
                                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                                        value={formData.intervalUnit}
                                        onChange={(e) => setFormData({ ...formData, intervalUnit: e.target.value })}
                                    >
                                        <option value="minutes">Minutes</option>
                                        <option value="hours">Hours</option>
                                        <option value="days">Days</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {formData.scheduleMode === 'custom' && (
                            <FormInput
                                id="cronExpression"
                                name="cronExpression"
                                label="Cron Expression"
                                value={formData.cronExpression || ''}
                                onChange={(e) => setFormData({ ...formData, cronExpression: e.target.value })}
                                placeholder="0 12 * * *"
                                helperText="Format: min hour day month weekday"
                            />
                        )}
                    </div>


                    <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                        <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                        <Button type="submit">{editingSchedule ? 'Save Changes' : 'Create Schedule'}</Button>
                    </div>
                </form>
            </Modal>

            <ConfirmModal
                isOpen={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={handleDelete}
                title="Delete Schedule?"
            >
                Are you sure you want to delete <strong>{confirmDelete?.name}</strong>? This action cannot be undone.
            </ConfirmModal>
        </StandardPageLayout>
    );
};

export default AutomationsPage;
